import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    Box,
    Typography,
    IconButton,
    TextField,
    InputAdornment,
    CircularProgress,
    Alert,
    Chip,
    List,
    ListItemButton,
    ListItemText,
    Divider,
    Stack,
    Snackbar,
    Tooltip,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Search as SearchIcon,
    Description as DescriptionIcon,
    PictureAsPdf as PdfIcon,
    WifiOff as WifiOffIcon,
    Email as EmailIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import sdsService from '../../services/sdsService';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';
import PdfPreviewModal from '../jha/pdf/PdfPreviewModal';

const SdsList = () => {
    const navigate = useNavigate();
    const isOnline = useOnlineStatus();
    const [items, setItems] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeCategory, setActiveCategory] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // PDF modal state
    const [pdfOpen, setPdfOpen] = useState(false);
    const [pdfBlob, setPdfBlob] = useState(null);
    const [pdfFilename, setPdfFilename] = useState('SDS.pdf');
    const [pdfLoading, setPdfLoading] = useState(false);
    const currentPdfIdRef = useRef(null);

    // Quick "email to me" from list row
    const [sendingId, setSendingId] = useState(null);
    const [snack, setSnack] = useState(null); // { type: 'success'|'error', text: string }

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                const data = await sdsService.getAll();
                if (!cancelled) setItems(data);
            } catch (err) {
                console.error('Error loading SDS list:', err);
                if (!cancelled) setError('Failed to load SDS records.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const categories = useMemo(() => {
        const set = new Set();
        for (const it of items) {
            if (it.category && it.category.trim()) set.add(it.category.trim());
        }
        return [...set].sort((a, b) => a.localeCompare(b));
    }, [items]);

    const filtered = useMemo(() => {
        let list = items;
        if (activeCategory) {
            list = list.filter(it => (it.category || '').trim() === activeCategory);
        }
        const q = searchTerm.trim().toLowerCase();
        if (q) {
            list = list.filter(it =>
                (it.name && it.name.toLowerCase().includes(q)) ||
                (it.description && it.description.toLowerCase().includes(q)) ||
                (it.category && it.category.toLowerCase().includes(q)) ||
                (it.sdsNum && it.sdsNum.toLowerCase().includes(q))
            );
        }
        return list;
    }, [items, searchTerm, activeCategory]);

    const handleOpenPdf = async (item) => {
        if (!isOnline) {
            setError('You are offline. SDS documents can only be viewed online.');
            return;
        }
        currentPdfIdRef.current = item.id;
        setPdfFilename(`${item.name || 'SDS-' + item.id}.pdf`);
        setPdfBlob(null);
        setPdfLoading(true);
        setPdfOpen(true);
        try {
            const blob = await sdsService.getPdfBlob(item.id);
            setPdfBlob(blob);
        } catch (err) {
            console.error('Failed to load SDS PDF:', err);
            const msg = err?.response?.status === 404
                ? 'Document file not found on the server.'
                : 'Failed to load PDF. Please try again.';
            setPdfOpen(false);
            setError(msg);
        } finally {
            setPdfLoading(false);
        }
    };

    const handleClosePdf = () => {
        setPdfOpen(false);
        setPdfBlob(null);
    };

    const handleEmailToMe = async () => {
        const id = currentPdfIdRef.current;
        if (!id) throw new Error('No SDS selected');
        return sdsService.emailToMe(id);
    };

    const handleQuickEmail = async (item, e) => {
        e.stopPropagation();
        if (!isOnline) {
            setSnack({ type: 'error', text: 'You are offline. Try again when online.' });
            return;
        }
        if (sendingId) return;
        setSendingId(item.id);
        try {
            const result = await sdsService.emailToMe(item.id);
            setSnack({ type: 'success', text: (result && result.message) || 'Email sent.' });
        } catch (err) {
            const msg = err?.response?.data?.message
                || (err?.response?.status === 429 ? 'Too many requests. Try again later.' : 'Failed to send email.');
            setSnack({ type: 'error', text: msg });
        } finally {
            setSendingId(null);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{
            position: 'fixed',
            top: '60px',
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            backgroundColor: 'background.default'
        }}>
            {/* Header */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 2,
                borderBottom: 1,
                borderColor: 'divider',
                backgroundColor: 'background.paper',
                flex: '0 0 auto'
            }}>
                <Box display="flex" alignItems="center" gap={2}>
                    <IconButton onClick={() => navigate('/')} color="primary" size="large">
                        <ArrowBackIcon />
                    </IconButton>
                    <Box>
                        <Typography variant="h5" fontWeight={600}>
                            <DescriptionIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                            Safety Data Sheets
                        </Typography>
                        <Typography variant="subtitle2" color="text.secondary">
                            {filtered.length} of {items.length} records
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {!isOnline && (
                <Alert severity="warning" icon={<WifiOffIcon />} sx={{ mx: 2, mt: 2, flex: '0 0 auto' }}>
                    Offline — list is available, but PDFs require an internet connection.
                </Alert>
            )}

            {error && (
                <Alert severity="error" onClose={() => setError(null)} sx={{ mx: 2, mt: 2, flex: '0 0 auto' }}>
                    {error}
                </Alert>
            )}

            {/* Search */}
            <Box sx={{ px: 2, py: 2, flex: '0 0 auto' }}>
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Search by name, description, category, or SDS#"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action" />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'background.paper' } }}
                />
            </Box>

            {/* Categories filter */}
            {categories.length > 0 && (
                <Box sx={{ px: 2, pb: 2, flex: '0 0 auto', overflowX: 'auto' }}>
                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                        <Chip
                            label="All"
                            color={activeCategory === null ? 'primary' : 'default'}
                            onClick={() => setActiveCategory(null)}
                            size="small"
                        />
                        {categories.map(cat => (
                            <Chip
                                key={cat}
                                label={cat}
                                color={activeCategory === cat ? 'primary' : 'default'}
                                onClick={() => setActiveCategory(cat)}
                                size="small"
                            />
                        ))}
                    </Stack>
                </Box>
            )}

            {/* List */}
            <Box sx={{ flex: '1 1 auto', overflowY: 'auto', backgroundColor: 'background.paper', mx: 2, mb: 2, borderRadius: 1, border: 1, borderColor: 'divider' }}>
                {filtered.length === 0 ? (
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        gap: 1,
                        p: 4
                    }}>
                        <DescriptionIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
                        <Typography variant="h6" color="text.secondary">
                            {items.length === 0 ? 'No SDS records yet' : 'No matching records'}
                        </Typography>
                        {items.length === 0 && (
                            <Typography variant="body2" color="text.secondary">
                                Records will appear after the next sync.
                            </Typography>
                        )}
                    </Box>
                ) : (
                    <List disablePadding>
                        {filtered.map((item, idx) => (
                            <React.Fragment key={item.id}>
                                {idx > 0 && <Divider component="li" />}
                                <ListItemButton onClick={() => handleOpenPdf(item)} sx={{ py: 1.5 }}>
                                    <ListItemText
                                        primary={
                                            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                                <Typography variant="subtitle1" fontWeight={500}>
                                                    {item.name || `SDS #${item.id}`}
                                                </Typography>
                                                {item.sdsNum && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {item.sdsNum}
                                                    </Typography>
                                                )}
                                            </Box>
                                        }
                                        secondary={
                                            <Box>
                                                {item.description && (
                                                    <Typography variant="body2" color="text.secondary" sx={{
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical'
                                                    }}>
                                                        {item.description}
                                                    </Typography>
                                                )}
                                                {item.category && (
                                                    <Chip
                                                        label={item.category}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{ mt: 0.5, height: 22 }}
                                                    />
                                                )}
                                            </Box>
                                        }
                                    />
                                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
                                        <Tooltip title="Email this SDS to me">
                                            <span>
                                                <IconButton
                                                    size="medium"
                                                    color="primary"
                                                    onClick={(e) => handleQuickEmail(item, e)}
                                                    disabled={sendingId === item.id || !isOnline}
                                                    sx={{ mr: 0.5 }}
                                                >
                                                    {sendingId === item.id
                                                        ? <CircularProgress size={20} />
                                                        : <EmailIcon />}
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <PdfIcon color="action" />
                                    </Stack>
                                </ListItemButton>
                            </React.Fragment>
                        ))}
                    </List>
                )}
            </Box>

            <PdfPreviewModal
                open={pdfOpen}
                onClose={handleClosePdf}
                pdfBlob={pdfBlob}
                filename={pdfFilename}
                loading={pdfLoading}
                onEmailToMe={handleEmailToMe}
            />

            <Snackbar
                open={!!snack}
                autoHideDuration={snack?.type === 'success' ? 4000 : 6000}
                onClose={() => setSnack(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                {snack ? (
                    <Alert
                        onClose={() => setSnack(null)}
                        severity={snack.type}
                        variant="filled"
                        sx={{ width: '100%' }}
                    >
                        {snack.text}
                    </Alert>
                ) : undefined}
            </Snackbar>
        </Box>
    );
};

export default SdsList;
